/* eslint-disable react-hooks/exhaustive-deps */
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Icon,
  SearchInput,
  Space,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { AddIcon, CloseSIcon } from '@tonic-one/react-icons';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import customConfig from 'src/config';

import { GlobalApi } from '@Apis';
import ErrorAlert from '../../../../components/Common/ErrorAlert';
import { useDisclosure } from '../../../../components/Common/Tooltip/TextEllipsis/utils';
import IconButton from '../../../../components/IconButton';
import { useOrgStore } from '../../../../models/useOrgModel';
import {
  ApprovedUsersProps,
  DeleteApprovedUsersDto,
} from '../../../../types/globalSetting';
import { PolicyTypeEnum } from '../../../Policy/utils/constants';
import DelModal from '../../../Report/ActionModal/del';
import LoadingBtn from '../../../Report/LoadingBtn';
import SmartTooltip from '../../../Report/SmartTooltip';
import AddUserModal from './AddUserModal';
import { PolicyType } from '../../../../types/app';
const ApprovedUsers = (props: {
  policyType: PolicyTypeEnum;
  withEditPermission: boolean;
  loading: boolean;
  setLoading: (loading: boolean) => void;
}) => {
  const [colorMode] = useColorMode();
  const { orgsMap } = useOrgStore();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation();
  const onAdd = () => {
    setAddModalVisible(true);
    console.log('onAdd');
  };
  const { orgId } = useOrgStore();
  const [items, setItems] = useState<ApprovedUsersProps[] | []>([]);
  const [allItems, setAllItems] = useState<ApprovedUsersProps[] | []>([]);
  const [delModalTitle, setDelModalTitle] = useState('Delete Approved Users');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [addModalVisible, setAddModalVisible] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);
  const [delModalContent, setDelModalContent] = useState<
    JSX.Element[] | undefined
  >(undefined);
  const [delLoading, setDelLoading] = useState(false);
  const search = (keyword: string) => {
    if (keyword !== '') {
      const temp = allItems.filter(
        (item) => item.name.toLowerCase().indexOf(keyword.toLowerCase()) > -1
      );
      setItems(temp);
    } else {
      setItems(allItems);
    }
  };
  const hoverBackgroundColor = (
    {
      dark: 'rgba(255, 255, 255, 0.12)',
      light: 'rgba(0, 0, 0, 0.12)',
    } as any
  )[colorMode];

  async function loadData() {
    try {
      props.setLoading(true);
      const data: ApprovedUsersProps[] = (
        await GlobalApi.getApprovedUsers(props.policyType)
      ).data;
      setItems(data);
      setAllItems(data);
    } catch (error: any) {
      setErrorMsg(t('common.apiException.internalError'));
    } finally {
      props.setLoading(false);
    }
  }

  useEffect(() => {
    (async function doCall() {
      await loadData();
    })();
  }, []);

  const columns: ColumnDef<ApprovedUsersProps>[] = useMemo(
    () => [
      {
        id: 'selection',
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            indeterminate={row.getIsSomeSelected()}
            onChange={row.getToggleSelectedHandler()}
          />
        ),
        size: 48,
      },
      {
        id: 'policyType',
        header: t('globalSetting.approvedUser.column.policyType'),
        cell: ({ row }) => {
          const { policyType } = row.original;
          const temp =
            policyType === 1
              ? t('globalSetting.approvedUser.table.policyType.atp')
              : t('globalSetting.approvedUser.table.policyType.dlp');
          return temp;
        },
        size: 254,
      },
      {
        id: 'name',
        header: t('globalSetting.approvedUser.column.name'),
        accessorKey: 'name',
        size: 159,
        style: {
          textAlign: 'right',
        },
      },
      {
        id: 'email',
        header: t('globalSetting.approvedUser.column.emailAddress'),
        accessorKey: 'email',
        // accessorKey: 'emails',
        size: 391,
        style: {
          textAlign: 'right',
        },
      },
      {
        id: 'companyGuid',
        header: t('globalSetting.approvedUser.column.organization'),
        cell: ({ row }) => {
          const { companyGuid } = row.original;
          return orgsMap.get(companyGuid) ? orgsMap.get(companyGuid) : '';
        },
        size: 211,
        style: {
          textAlign: 'right',
        },
      },
    ],
    []
  );
  const [rowSelection, setRowSelection] = useState({});
  const table = useReactTable({
    data: items,
    columns,
    defaultColumn: {
      minSize: 40,
    },
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => `${row.email}|${row.policyType}`,
    state: {
      rowSelection,
    },
    enableRowSelection: true, // enable row selection for all rows
    // enableRowSelection: row => row.original.detections > 0, // or enable row selection conditionally per row
    onRowSelectionChange: setRowSelection,
  });
  const cellHeight = 36;
  const headerHeight = 36;
  const rowsToDisplay = 5;
  const tableStyleProps = {
    height: headerHeight + rowsToDisplay * cellHeight,
    // Uncomment the following line to specify the width and enable horizontal scrollbar
    // width: 200,
  };
  const selectedRowCount = Object.keys(rowSelection).length;
  const handleDel = () => {
    setDelModalTitle(t('globalSetting.approvedUser.actions.delete.title'));
    const selectedRows = getSelectedRows();
    const content = selectedRows.map((item) => (
      <Text key={item.email + item.policyType} mb={8}>
        {item.policyType === 1
          ? t('globalSetting.approvedUser.table.policyType.atp')
          : t('globalSetting.approvedUser.table.policyType.dlp')}
        | {item.email}
      </Text>
    ));
    setDelModalContent(content);
    onOpen();
  };
  const reset = () => {
    setRowSelection({});
    search('');
  };

  function getSelectedRows() {
    const selectedRows: DeleteApprovedUsersDto[] = [];
    for (const key of Object.keys(rowSelection)) {
      const [email, policyType] = key.split('|');
      const item = items.find(
        (item) => item.email === email && item.policyType === Number(policyType)
      );
      if (item) {
        selectedRows.push(item);
      }
    }
    return selectedRows;
  }

  const confirmDel = async () => {
    setDelLoading(true);
    if (!selectedRowCount) return;
    try {
      const selectedRows = getSelectedRows();
      const res = await GlobalApi.deleteApprovedUsers(
        PolicyTypeEnum.Exchange,
        selectedRows
      );
      if (res.data?.deleteCount) {
        reset();
        window.showNotification({
          titleMsg: t('globalSetting.approvedUser.delete.success'),
          severity: 'success',
        });
      }
    } catch (error: any) {
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    } finally {
      onClose();
      setDelLoading(false);
      await loadData();
    }
  };
  return (
    <Box>
      <Box>
        <DelModal
          visible={isOpen}
          onClose={onClose}
          onConfirm={confirmDel}
          loading={delLoading}
          title={delModalTitle}
          content={delModalContent}
          withEditPermission={props.withEditPermission}
        ></DelModal>
        <AddUserModal
          policyType={PolicyType.Exchange}
          policySubType={0}
          withEditPermission={props.withEditPermission}
          visible={addModalVisible}
          onReload={loadData}
          orgId={orgId}
          loading={props.loading}
          setLoading={props.setLoading}
          onClose={() => {
            setAddModalVisible(false);
          }}
        />
        {errorMsg && (
          <ErrorAlert
            errorMsg={errorMsg}
            onClose={() => setErrorMsg(undefined)}
          />
        )}
        <Flex m="16px 0px">
          <Text fontSize="lg" lineHeight="lg" color={colorStyle.color.primary}>
            {t('globalSetting.approvedUser.tab.title')}
          </Text>
        </Flex>
        <Flex m="16px 0px">
          <Text
            color={colorStyle.color.secondary}
            lineHeight="lg"
            maxWidth={1000}
          >
            {t('globalSetting.approvedUser.description.content', {
              count:
                customConfig.ui.globalSettings.limitation.approvedUserMaxLength,
            })}
          </Text>
        </Flex>
        <Flex m="16px 0px">
          <Text
            color={colorStyle.color.secondary}
            lineHeight="lg"
            maxWidth={1000}
          >
            {t('globalSetting.approvedUser.description.note')}
          </Text>
        </Flex>
        <Flex m="16px 0px">
          <Text>
            <Space>{t('globalSetting.note.title')}</Space>
            <Space width="1x"></Space>
            <Space color={colorStyle.color.secondary}>
              {t('globalSetting.note.applyToAllOrganizations')}
            </Space>
          </Text>
        </Flex>
        <Flex alignItems="center">
          {selectedRowCount !== 0 ? (
            <Box mr={16}>
              <Stack direction="row">
                <LoadingBtn
                  variant="secondary"
                  mr="4x"
                  onClick={handleDel}
                  disabled={!props.withEditPermission}
                >
                  {t('reportCommon.buttons.delete')}
                </LoadingBtn>
                <IconButton onClick={reset}>
                  <Icon as={CloseSIcon}></Icon>
                </IconButton>
                <Box lineHeight="2rem">
                  <Text color={colorStyle.color.tertiary}>
                    {selectedRowCount}&nbsp;
                    {t('common.label.selected')}
                  </Text>
                </Box>
              </Stack>
            </Box>
          ) : null}
          {selectedRowCount === 0 ? (
            <Stack direction="row">
              <Button
                variant="primary"
                onClick={onAdd}
                disabled={
                  !props.withEditPermission ||
                  items.length >=
                    customConfig.ui.globalSettings.limitation
                      .approvedUserMaxLength
                }
              >
                <Icon as={AddIcon} mr={8} />
                {t('globalSetting.approvedUser.action.label.add')}
              </Button>
              <Space width="12px" />
              <SearchInput
                placeholder={t(
                  'globalSetting.approvedUser.searchInput.placeholder'
                )}
                onChange={(e: any) => {
                  search(e.target.value);
                }}
                onClearInput={() => {
                  search('');
                }}
                width="374px"
              />
            </Stack>
          ) : null}
        </Flex>
        <Table mt={28} layout="flexbox" {...tableStyleProps}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableHeaderRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const styleProps = {
                    minWidth: header.column.columnDef.minSize,
                    width: header.getSize(),
                  };
                  return (
                    <TableHeaderCell key={header.id} {...styleProps}>
                      {header.isPlaceholder ? null : (
                        <SmartTooltip>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </SmartTooltip>
                      )}
                    </TableHeaderCell>
                  );
                })}
              </TableHeaderRow>
            ))}
          </TableHeader>
          <TableBody overflowY="scroll">
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                _hover={{
                  backgroundColor: hoverBackgroundColor,
                }}
              >
                {row.getVisibleCells().map((cell) => {
                  const styleProps = {
                    minWidth: cell.column.columnDef.minSize,
                    width: cell.column.getSize(),
                  };
                  return (
                    <TableCell key={cell.id} {...styleProps}>
                      <SmartTooltip>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </SmartTooltip>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};
export default React.memo(ApprovedUsers);
